import { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, NavLink, useNavigate, Navigate } from 'react-router-dom';
import { ReactComponent as KaizIQText } from '@Assets/kaiziqText.svg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import {
    LayoutDashboard,
    Users,
    MessageSquare,
    Settings,
    ChevronLeft,
    ChevronRight,
    BookMinus,
    UserRound,
    LogOut,
    Building2,
    Plus,
    CircleCheckBig,
    LayoutTemplate,
    UserCog,
    FileText,
    Brain,
    ChevronUp,
    ChevronDown,
    BotMessageSquare,
    NotebookTabs,
    Spotlight,
    HatGlasses,
    PanelLeftClose,
    PanelRightClose
} from 'lucide-react';
import { SiGoogleadsense as LeadsIcon } from "react-icons/si";
import { MenuItem, Menu, Divider, Tooltip } from '@mui/material';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import KaiziqLeadsConnectionService from '@Helpers/URLHelpers/WeboutService/KaiziqLeadsConnectionService';
import './SidePanel.scss';

const whatsappMenu = [
    {
        id: 1,
        title: "Chats",
        icon: <MessageSquare size={18} />,
        link: "/home/whatsapp",
        hidden: false
    },
    {
        id: 2,
        title: "Campaigns",
        icon: <Spotlight size={18} />,
        link: "/home/whatsapp/campaigns",
        hidden: false
    },
    {
        id: 3,
        title: "Templates",
        icon: <LayoutTemplate size={18} />,
        link: "/home/whatsapp/templates",
        hidden: false
    },
    {
        id: 5,
        title: "Auto Replies",
        icon: <BotMessageSquare size={18} />,
        link: "/home/whatsapp/auto-replies",
        hidden: false
    },
    {
        id: 6,
        title: "Business Info",
        icon: <Building2 size={18} />,
        link: "/home/whatsapp/business-info",
        hidden: false
    },
    {
        id: 7,
        title: "Settings",
        icon: <Settings size={18} />,
        link: "/home/whatsapp/settings",
        hidden: false,
    },
    {
        id: 8,
        title: "AI Agent",
        icon: <Brain size={18} />,
        link: "/home/whatsapp/ai-agent",
        hidden: true,
    },
];

function SidePanel({ isOpen, setIsOpen }) {

    const location = useLocation();
    const navigate = useNavigate();
    const [orgList, setOrgList] = useState([]);
    const [cursor, setCursor] = useState('auto');
    const [anchorMenu, setAnchorMenu] = useState(null);
    const [showMenu, setShowMenu] = useState(false);
    const [anchorOrganizationMenu, setAnchorOrganizationMenu] = useState(null);
    const [showOrganizationOptions, setShowOrganizationOptions] = useState(false);
    const [showAddOrganizationForm, setShowAddOrganizationForm] = useState(false);
    const { currentOrg, currentOrgId, setCurrentOrg } = useReduxOrg();
    const { setCurrentProject, currentProject, clearCurrentProject } = useReduxProject();
    const { currentLeads, setLeadsList, setCurrentLeads, clearLeads, leadsPaymentInfo, setNumbersList } = useReduxLeads();
    const { activeUserId, activeUser, activeUserAccessList } = useReduxActiveUser();
    const [showSubMenu, setShowSubMenu] = useState(true);
    const menuItems = [
        { icon: <LayoutDashboard size={18} />, label: 'Dashboard', path: '/home', submenu: false, menuList: [] },
        { icon: <Users size={18} />, label: 'Leads', path: '/home/leads', submenu: false, menuList: [] },
        { icon: <MessageSquare size={18} />, label: 'WhatsApp', path: '/home/whatsapp', submenu: true, menuList: whatsappMenu },
        //        { icon: <Mail size={18} />, label: 'Email Campaigns', path: '/home/emails' },
        //        { icon: <BarChart3 size={18} />, label: 'Tracking', path: '/home/tracking' },
        { icon: <FileText size={18} />, label: 'Files', path: '/home/files', submenu: false, menuList: [] },
        { icon: <NotebookTabs size={18} />, label: 'Contacts', path: '/home/contacts', submenu: false, menuList: [] },
        { icon: <HatGlasses size={18} />, label: 'Agents', path: '/home/agents', submenu: false, menuList: [] },
    ];
    const kaiziqLeadsConnectionService = new KaiziqLeadsConnectionService();
    const className = 'sidePanel';

    useEffect(() => {
        renderData();
    }, []);

    useEffect(() => {
        let locationPath = location.pathname;
        let checkSubMenu = menuItems.find((item)=> item.submenu && locationPath.startsWith(item.path));
        if (!!checkSubMenu) {
            setShowSubMenu(true);
        }
    }, [location.pathname]);

    const renderData = async () => {
        setCursor('wait');
        let orgList = [];
        let leadsOrg = activeUserAccessList?.orgs?.filter(org => org.visibility === 'leads');
        let projects = activeUserAccessList?.projects?.filter((project) => project.visibility === 'leads');
        for (let i = 0; i < leadsOrg.length; i++) {
            let activeOrg = leadsOrg[i];
            let getProjectsInOrg = projects.filter((item) => (item.parentId === activeOrg?.id && item.parentType === 'org') && item);
            if (getProjectsInOrg.length !== 0) {
                orgList.push(activeOrg);
            }
        }
        setOrgList(orgList);
        setCursor('auto');
    }

    const userName = activeUser?.fullName || activeUser?.userName || 'User';
    const initials = useMemo(() => {
        const parts = (userName || 'U').trim().split(/\s+/);
        const first = parts[0]?.[0] || '';
        const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
        return (first + (last || '')).toUpperCase();
    }, [userName]);


    const handleOrgChange = (org) => {
        handleOrganizationPopup();
        handleOptionsPopup();
        setCurrentOrg(org);
        changeOrgAndProject(org);
    }

    const changeOrgAndProject = async (activeOrg) => {
        setCursor('wait');
        clearLeads();
        clearCurrentProject();
        if (activeUserAccessList?.projects?.length > 0) {
            let projects = !!activeUserAccessList.projects ? activeUserAccessList.projects : [];
            let getProjectsInOrg = projects.filter((item) => (item.parentId === activeOrg?.id && item.parentType === 'org') && item);
            let projectList = getProjectsInOrg.filter((item) => item.visibility === 'leads');
            let activeProject = projectList.at(-1);
            if (!!activeProject) {
                setCurrentProject(activeProject);
                let response = await kaiziqLeadsConnectionService.getLeadsAndPhoneNumberListByProjectId(activeProject.id);
                if(response.status < 400) {
                    response = JSON.parse(response.body);
                    let leadsList = response.leadsList;
                    let phoneNumbers = response.phoneNumberList;
                    setLeadsList(leadsList);
                    if(leadsList.length > 0) {
                        setCurrentLeads(leadsList[0]);
                        setNumbersList(phoneNumbers);
                    }
                }
            }
        }
        setCursor('auto');
        navigate('/home');
    }

    const handleOptionsPopup = (event) => {
        if (!showMenu) {
            if (!!event) {
                setAnchorMenu(event.currentTarget);
                setShowMenu(true);
            }
        } else {
            setAnchorMenu(null);
            setShowMenu(false);
        }
    }

    const handleOrganizationPopup = (event) => {
        if (!showOrganizationOptions) {
            if (!!event) {
                setAnchorOrganizationMenu(event.currentTarget);
                setShowOrganizationOptions(true);
            }
        } else {
            setAnchorOrganizationMenu(null);
            setShowOrganizationOptions(false);
        }
    }

    return (
        <div className={`sidebar-wrapper ${isOpen ? 'open' : ''}`} style={{cursor: cursor}}>
            <aside className={`sidebar-root ${isOpen ? 'open' : 'closed'}`} >
                <nav className="sidebar-menu">
                    {menuItems.map((item) => {
                        const isActive = item.path === '/home' ? location.pathname === '/home' : location.pathname.startsWith(item.path);
                        return (
                            <>
                                {isOpen ?
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`sidebar-menu-item ${isActive ? 'active' : ''}`}
                                        onClick={() => item.submenu && setShowMenu(!showSubMenu)}>
                                        <span className={`sidebar-menu-icon ${isActive ? 'active' : ''}`}>{item.icon}</span>
                                        <span className={`sidebar-menu-label ${isOpen ? 'show' : 'hide'}`}>{item.label}</span>
                                        {item.submenu &&
                                            isOpen &&
                                            (showMenu ? (
                                                <ChevronUp size={16} style={{marginLeft: 'auto'}} />
                                            ) : (
                                                <ChevronDown size={16} style={{marginLeft: 'auto'}} />
                                            ))
                                        }
                                    </Link>
                                :
                                    !item.submenu &&
                                        <Tooltip title={item.label} arrow placement="right" key={item.path}>
                                            <Link
                                                to={item.path}
                                                className={`sidebar-menu-item ${isActive ? 'active' : ''}`}
                                                onClick={() => item.submenu && setShowSubMenu(!showSubMenu)}>
                                                <span className={`sidebar-menu-icon ${isActive ? 'active' : ''}`}>{item.icon}</span>
                                                <span className={`sidebar-menu-label ${isOpen ? 'show' : 'hide'}`}>{item.label}</span>
                                                {item.submenu &&
                                                    isOpen &&
                                                    (showMenu ? (
                                                        <ChevronUp size={16} style={{marginLeft: 'auto'}} />
                                                    ) : (
                                                        <ChevronDown size={16} style={{marginLeft: 'auto'}} />
                                                    ))
                                                }
                                            </Link>
                                        </Tooltip>
                                }
                                {item.submenu && showSubMenu &&
                                    item.menuList?.map((item) => {
                                        const active = location.pathname === item.link;
                                        const hideSettings = false;
                                        const hidden = item.hidden ? item.hidden : (item.title === 'Settings' ? hideSettings : item.hidden);
                                        return (
                                            !hidden &&
                                                <Tooltip title={isOpen ? '' : item.title} arrow placement="right" key={item.link}>
                                                    <div className={`sidebar-submenu`} style={{paddingTop: isOpen && '5px', paddingLeft: isOpen && '26px'}}>
                                                        <NavLink
                                                            to={item.link}
                                                            end={item.link}
                                                            className={`sidebar-submenu-item ${isOpen ? 'sidebar-submenu-item-open' : 'sidebar-submenu-item-hide'} ${active ? "active" : ""}`}>
                                                            <span className={`sidebar-menu-label`}>
                                                                {item.icon}
                                                            </span>
                                                            <span className={`sidebar-menu-label ${isOpen ? 'show' : 'hide'}`}>{item.title}</span>
                                                        </NavLink>
                                                    </div>
                                                </Tooltip>
                                        );
                                    })
                                }
                            </>
                        );
                    })}
                </nav>
                <div className="sidebar-footer">
                    <Tooltip title={isOpen ? '' : (currentOrg?.orgName ?? 'organization')} arrow placement="right">
                        <div className={`sidebar-menu-item`} onClick={handleOrganizationPopup}>
                            <span> <Building2 size={17} className="sidebar-menu-icon" /> </span>
                            <span className={`sidebar-menu-label ${isOpen ? 'show' : 'hide'} truncate`}>{currentOrg?.orgName ?? 'organization'}</span>
                        </div>
                    </Tooltip>
                    <Tooltip title={isOpen ? '' : 'User Settings'} arrow placement="right">
                        <Link to="/home/userManagement/settings" className={`sidebar-menu-item ${location.pathname === '/home/userManagement/settings' ? 'active' : ''}`}>
                            <span> <UserCog size={18} className="sidebar-menu-icon" /></span>
                            <span className={`sidebar-menu-label ${isOpen ? 'show' : 'hide'}`}>User Settings</span>
                        </Link>
                    </Tooltip>
                    <Tooltip title={isOpen ? '' : 'Expand'} arrow placement="right">
                        <div className={`sidebar-menu-item`} onClick={()=> setIsOpen(!isOpen)}>
                            <span>
                                {
                                    isOpen ?
                                        <PanelLeftClose size={17} className="sidebar-menu-icon" />
                                    :
                                        <PanelRightClose size={17} className="sidebar-menu-icon" />
                                }
                            </span>
                            <span className={`sidebar-menu-label ${isOpen ? 'show' : 'hide'} truncate`}>
                                {isOpen ? 'Collapse' : 'Expand'}
                            </span>
                        </div>
                    </Tooltip>
                </div>
                {showOrganizationOptions && (
                    <Menu
                        open={showOrganizationOptions}
                        onClose={handleOrganizationPopup}
                        anchorEl={anchorOrganizationMenu}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            elevation: 0,
                            className: `${className}__orgMenuPaper`,
                        }}
                    >
                        <div className={`${className}__orgMenu`}>
                            <div className={`${className}__orgMenu__header`}>
                                <div className={`${className}__orgMenu__header__icon`}>
                                    <Building2 size={16} />
                                </div>
                                <div className={`${className}__orgMenu__header__content`}>
                                    <span className={`${className}__orgMenu__header__title`}>
                                        Switch Organization
                                    </span>
                                    <span className={`${className}__orgMenu__header__subtitle`}>
                                        Choose an organization to continue
                                    </span>
                                </div>
                            </div>
                            <div className={`${className}__orgMenu__divider`} />
                            <div className={`${className}__orgMenu__list`}>
                                {orgList?.map((org) => {
                                    const isSelected = org.id === currentOrg?.id;
                                    return (
                                        <MenuItem
                                            key={org.id}
                                            className={`${className}__orgMenu__item ${
                                                isSelected
                                                    ? `${className}__orgMenu__item--selected`
                                                    : ''
                                            }`}
                                            onClick={() => handleOrgChange(org)}
                                        >
                                            <div className={`${className}__orgMenu__item__avatar`}>
                                                {org.tag?.charAt(0)?.toUpperCase() ||
                                                    org.orgName?.charAt(0)?.toUpperCase()}
                                            </div>
                                            <div className={`${className}__orgMenu__item__content`}>
                                                <div className={`${className}__orgMenu__item__name`}>
                                                    {org.orgName}
                                                </div>
                                                {org.tag && (
                                                    <div className={`${className}__orgMenu__item__tag`}>
                                                        {org.tag}
                                                    </div>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <div
                                                    className={`${className}__orgMenu__item__check`}
                                                >
                                                    <CircleCheckBig size={18} />
                                                </div>
                                            )}
                                        </MenuItem>
                                    );
                                })}
                            </div>
                        </div>
                    </Menu>
                )}
            </aside>
        </div>
    );
}

export default SidePanel;
